/**
 * Key pickup and key return, as the design draws them (COL-533).
 *
 * These two cards are the whole point of consolidating check-in/out: the key
 * information was the one thing V1's standalone screen carried that nothing
 * else did, so it moves here, beneath the handover row it belongs to, and the
 * duplicate screen goes.
 *
 * Four states, from the design:
 *   pickup, locked    -- "Unlocks <date>, 00:01 · Berlin time" + a countdown.
 *   pickup, unlocked  -- the location, address and access details.
 *   return, unscheduled -- "Not scheduled yet", with what will make it appear.
 *   return, scheduled -- the return instructions.
 *
 * The unlock instant comes from `keynest-unlock`, lifted out of V1 so both
 * screens read ONE rule. A second implementation would eventually disagree,
 * and the failure is asymmetric: revealing a pickup code early is a security
 * problem, hiding it late strands a tenant at the shop.
 */
import { useEffect, useState } from "react";

import {
  keynestUnlockAt,
  keynestUnlocked,
  PROPERTY_TIME_ZONE,
} from "@/lib/keynest-unlock";
import type { TenantKeyPickup } from "@/hooks/useTenantKeyPickup";

type Labels = Record<string, string>;

/** Beyond this, a ticking clock is noise rather than information. */
const COUNTDOWN_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * `HH:MM:SS` remaining, or null when the instant has passed OR is more than a
 * day away.
 *
 * The window matters. Pickup unlocks the day before move-in, but this card
 * renders from the moment a booking exists — so a tenant moving in next month
 * would otherwise watch a counter read "743:21:08", which is both absurd and
 * wrong-looking (hours never wrap into days). Outside the window the card
 * states the unlock date and leaves it at that; the clock appears when it is
 * actually imminent, which is what the design shows.
 */
export function countdownTo(target: Date | null, nowMs: number): string | null {
  if (!target) return null;
  const ms = target.getTime() - nowMs;
  if (ms <= 0 || ms > COUNTDOWN_WINDOW_MS) return null;
  const total = Math.floor(ms / 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(total / 3600))}:${pad(
    Math.floor((total % 3600) / 60)
  )}:${pad(total % 60)}`;
}

function Shell({
  tone,
  label,
  children,
}: {
  /** White for something live, muted for something not yet scheduled. */
  tone: "live" | "muted";
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={[
        "mt-3 rounded-2xl px-5 py-4",
        tone === "live" ? "bg-background" : "bg-background/40",
      ].join(" ")}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

export function KeyPickupCard({
  booking,
  pickup,
  d,
  locale,
}: {
  booking: any;
  pickup: TenantKeyPickup;
  d: Labels;
  /*
    The unlock instant is always the Berlin wall-clock rule
    (`keynestUnlockAt`/`PROPERTY_TIME_ZONE`), but a bare
    `unlockAt.toLocaleDateString()` renders it in the BROWSER's locale and
    timezone -- so a tenant viewing from outside Germany would see a date
    shifted by the zone difference, same trap `billedMonth` guards against
    for rent rows. `locale` picks the format; the timeZone option below
    pins it back to Berlin regardless of where the tenant is.
  */
  locale: string;
}) {
  const unlockAt = keynestUnlockAt(booking);
  const [nowMs, setNowMs] = useState(() => Date.now());
  /*
    Locked state comes from the shared rule, NOT from "is a countdown
    showing". They are different questions: a move-in next month is locked
    with no countdown, and conflating them would reveal the pickup code to
    every tenant whose move-in is more than a day away.
  */
  const locked = !keynestUnlocked(booking, nowMs);
  const remaining = countdownTo(unlockAt, nowMs);

  /*
    Tick only while locked, so the countdown runs down and the card flips at
    00:01 without a reload — and nothing ticks for the rest of the tenancy.
  */
  useEffect(() => {
    if (!locked) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [locked]);

  if (locked) {
    return (
      <Shell tone="live" label={d.keyPickupLabel}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-semibold text-foreground">
            {String(d.keyPickupUnlocksAt ?? "").replace(
              "{datetime}",
              unlockAt
                ? `${unlockAt.toLocaleDateString(locale, {
                    timeZone: PROPERTY_TIME_ZONE,
                  })}, 00:01`
                : ""
            )}
          </p>
          {/*
            `tabular-nums` so the digits do not jitter the layout each second,
            and aria-live off: a counter announced every second is unusable
            with a screen reader. The sentence below says what will happen.
          */}
          {remaining ? (
            <span className="shrink-0 rounded-full bg-muted px-3 py-1.5 font-mono text-sm font-semibold tabular-nums text-foreground">
              {remaining}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {d.keyPickupLockedHint}
        </p>
      </Shell>
    );
  }

  /*
    "Not arranged yet" is an ANSWER, and we only have one once the query has
    given us one. `hasAny` is false while the request is in flight and false
    again after it fails, so reading it alone told a tenant whose request had
    just failed that their key handover is not set up -- days before a
    move-in, that is the one thing they must not be told wrongly. Loading and
    failure each get their own state instead. (Ganesh, 14 Oct)
  */
  if (pickup.isPending) {
    return (
      <Shell tone="muted" label={d.keyPickupLabel}>
        <p className="text-muted-foreground">{d.keyPickupLoading}</p>
      </Shell>
    );
  }
  if (pickup.isError) {
    return (
      <Shell tone="muted" label={d.keyPickupLabel}>
        <p className="text-destructive">{d.keyPickupFailed}</p>
      </Shell>
    );
  }

  // Unlocked, answered, and nothing configured: say so rather than render an
  // empty card.
  if (!pickup.hasAny) {
    return (
      <Shell tone="muted" label={d.keyPickupLabel}>
        <p className="font-semibold text-foreground">{d.keyPickupPending}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {d.keyPickupPendingHint}
        </p>
      </Shell>
    );
  }

  const manual = pickup.pickup?.method === "manual";
  // KeyNest pickups with nothing written get the default, in the tenant's
  // language; custom text is shown as the admin wrote it.
  const instructions =
    pickup.pickup?.instructions ||
    (pickup.pickup?.method === "keynest" ? d.keyNestDefaultInstructions : null);

  return (
    <Shell tone="live" label={d.keyPickupLabel}>
      {pickup.pickup?.location ? (
        <p className="font-semibold text-foreground">
          {pickup.pickup.location}
        </p>
      ) : null}
      {pickup.pickup?.address ? (
        <p className="mt-0.5 text-sm text-muted-foreground">
          {pickup.pickup.address}
        </p>
      ) : null}
      {pickup.keynest.lockerNumber ||
      pickup.keynest.pickupCode ||
      (manual && pickup.keynest.accessCode) ? (
        <p className="mt-2 text-sm text-foreground">
          {[
            pickup.keynest.lockerNumber
              ? `${d.keyLocker}: ${pickup.keynest.lockerNumber}`
              : null,
            pickup.keynest.pickupCode
              ? `${d.keyCode}: ${pickup.keynest.pickupCode}`
              : null,
            // A manual pickup has no KeyNest page to fetch the code from.
            manual && pickup.keynest.accessCode
              ? `${d.keyAccessCode}: ${pickup.keynest.accessCode}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}
      {instructions ? (
        <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
          {instructions}
        </p>
      ) : null}
      {pickup.keynest.accessUrl ? (
        <a
          href={pickup.keynest.accessUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-block text-sm font-semibold underline underline-offset-4"
        >
          {d.keyPickupOpenLink}
        </a>
      ) : null}
    </Shell>
  );
}

export function KeyReturnCard({
  moveOutConfirmed,
  instructions,
  d,
}: {
  /**
   * Only a CONFIRMED move-out date gets return instructions. A booking's
   * `move_out_date` doubles as the contract end for an ongoing tenancy, so
   * treating it as "scheduled" would tell a tenant living there to hand their
   * keys back — the same trap `moveOutNote` already guards against.
   */
  moveOutConfirmed: boolean;
  instructions: string | null;
  d: Labels;
}) {
  if (!moveOutConfirmed) {
    return (
      <Shell tone="muted" label={d.keyReturnLabel}>
        <p className="font-semibold text-foreground">{d.keyReturnNotYet}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {d.keyReturnNotYetHint}
        </p>
      </Shell>
    );
  }

  return (
    <Shell tone="live" label={d.keyReturnLabel}>
      <p className="font-semibold text-foreground">{d.keyReturnScheduled}</p>
      <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
        {instructions || d.keyReturnDefaultHint}
      </p>
    </Shell>
  );
}
